import axios from 'axios';
import { writeFieldValue } from '../../../utils/hmiFieldApi';

const API_BASE = '/machinesim/api';

/**
 * Creates read/write helpers for HMI fields. Preview mode never hits REST.
 *
 * Writes are field-key GENERIC: `apply` / `setValueDebounced` take the field key
 * and route through `writeFieldValue`. There are no per-field (feedrate/override/…)
 * special cases here — that keeps a NEW machine-type field writable with no code.
 *
 * @param {object} ctx
 * @param {boolean} ctx.previewMode
 * @param {string} ctx.machineName
 * @param {function} ctx.setLiveState
 * @param {import('react').MutableRefObject<Set<string>>} [ctx.pendingRef]
 *   Set of field keys with an in-flight debounced write. The panel consults this
 *   so a 1s server push doesn't clobber a value the operator is actively dragging.
 * @param {import('react').MutableRefObject<(() => Promise<boolean>) | null | undefined>} [ctx.mesEstopUserConfirmRef]
 *   When set, called before an E-stop POST; return false to abort.
 * @returns {{
 *   apply: (fieldKey: string, next: unknown) => Promise<void>,
 *   setValueDebounced: (fieldKey: string, next: unknown, delay?: number) => void,
 *   startCycle: () => Promise<void>,
 *   emergencyStop: () => Promise<void>,
 *   resetAllToolCounts: () => Promise<void>,
 *   dispose: () => void,
 * }}
 */
export function createActionBridge(ctx) {
  const { previewMode, machineName, setLiveState, pendingRef, mesEstopUserConfirmRef } = ctx;

  const timers = new Map();
  const markPending = (key) => pendingRef?.current?.add?.(key);
  const clearPending = (key) => pendingRef?.current?.delete?.(key);

  const apply = async (fieldKey, next) => {
    setLiveState((s) => ({ ...s, [fieldKey]: next }));
    if (previewMode) return;
    const req = writeFieldValue(machineName, fieldKey, next);
    if (!req) {
      console.warn('No REST route for field', fieldKey);
      return;
    }
    try {
      await req;
    } catch (e) {
      console.error(e);
    }
  };

  const setValueDebounced = (fieldKey, next, delay = 300) => {
    setLiveState((s) => ({ ...s, [fieldKey]: next }));
    if (previewMode) return;
    markPending(fieldKey);
    if (timers.has(fieldKey)) clearTimeout(timers.get(fieldKey));
    timers.set(
      fieldKey,
      setTimeout(() => {
        timers.delete(fieldKey);
        const req = writeFieldValue(machineName, fieldKey, next);
        Promise.resolve(req)
          .catch((e) => console.error(e))
          .finally(() => clearPending(fieldKey));
      }, delay)
    );
  };

  /**
   * "CYCLE START" — kick off engine-managed production for this machine.
   * Routes to `POST /sim/:machine/start`, the documented successor to the legacy
   * per-machine `/run-cycle` (now a 410 stub).
   *
   * `actionStart` returns `{ success:false, error }` at HTTP 200 when the phase
   * isn't IDLE (engine REFUSED) — axios does NOT reject on that. So we only flip
   * execution to ACTIVE when `success !== false`, otherwise we'd show a phantom
   * RUNNING until the next socket reconcile. Same guard as `resetAllToolCounts`.
   */
  const startCycle = async () => {
    if (previewMode) {
      setLiveState((s) => ({ ...s, execution: 'ACTIVE' }));
      return;
    }
    try {
      const res = await axios.post(`${API_BASE}/sim/${encodeURIComponent(machineName)}/start`);
      if (res?.data && res.data.success === false) {
        console.warn('CYCLE START refused:', res.data.error);
        return; // engine declined — let the socket drive truth, no optimistic flip
      }
      setLiveState((s) => ({ ...s, execution: 'ACTIVE' }));
    } catch (e) {
      console.error('CYCLE START failed:', e);
    }
  };

  const emergencyStop = async () => {
    if (previewMode) {
      setLiveState((s) => ({ ...s, execution: 'READY' }));
      return;
    }
    const confirmGate = mesEstopUserConfirmRef?.current;
    if (typeof confirmGate === 'function') {
      const proceed = await confirmGate();
      if (!proceed) return;
    }
    await axios.post(`${API_BASE}/${encodeURIComponent(machineName)}/emergency-stop`);
    setLiveState((s) => ({ ...s, execution: 'READY' }));
  };

  /** Cancel all pending debounced writes — call on unmount / bridge recreation. */
  const dispose = () => {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    if (pendingRef?.current?.clear) pendingRef.current.clear();
  };

  /**
   * "I changed these tools" → zero their life counters. Routes to the ENGINE's
   * key-based tool-change action (`POST /sim/:machine/tool-change { positions }`),
   * which zeros `ts.tools` by KEY (T1/DIE/BROACH all work), persists, and resumes
   * production if the machine was holding in TOOL_CHANGE. The old
   * `/reset-tool-life` path wrote machine.state directly — the engine overwrote it
   * next tick and it was numeric-T-gated, so DIE/BROACH never reset.
   * @param {string[]} keys tool keys from extractTlmTools (e.g. ['T1','DIE'])
   */
  const resetAllToolCounts = async (keys = []) => {
    if (previewMode) {
      setLiveState((s) => {
        const next = { ...s };
        for (const k of Object.keys(next)) {
          if (/^TLM_.+_COUNT$/i.test(k)) next[k] = 0;
        }
        return next;
      });
      return;
    }
    const positions = Array.isArray(keys) ? keys.filter(Boolean) : [];
    if (positions.length === 0) return;
    try {
      const res = await axios.post(
        `${API_BASE}/sim/${encodeURIComponent(machineName)}/tool-change`,
        { positions }
      );
      // Engine returns { success:false, error } when there's no live tool state
      // (e.g. not running a tooled op) — surface it, don't crash.
      if (res?.data && res.data.success === false) {
        console.warn('tool-change reset skipped:', res.data.error);
      }
    } catch (e) {
      console.error('tool-change reset failed:', e);
    }
  };

  return { apply, setValueDebounced, startCycle, emergencyStop, resetAllToolCounts, dispose };
}
